/**
 * Central route definitions and path parsing for the omni-app.
 * All path matching and param extraction for the app shell happens here.
 * See AGENT.md "Routing" for the full route table.
 */

export type AppMode = 'chat' | 'cowork' | 'platform';

/**
 * Campaign detail URL uses `content-approval`, `briefs-beats` and `results`; legacy `/drafts` and `/summary`
 * redirect to those segments. `overview` (empty segment) is the campaign board; `briefsBeats` is the former
 * overview (campaign brief + beats list + beat brief).
 */
export type CampaignSubTab =
  | 'overview'
  | 'briefsBeats'
  | 'profiles'
  | 'collaborations'
  | 'contentApproval'
  | 'publications'
  | 'results';

export type CurrentPage =
  | 'dashboard'
  | 'news-monitor'
  | 'performance'
  | 'brand-pulse'
  | 'marketing-mix-model-mmm'
  | 'creators'
  | 'creatorslist'
  | 'creatorlist-detail'
  | 'creator-profile'
  | 'discovery-creatorlist'
  | 'search-results'
  | 'audience-insights'
  | 'trends'
  | 'see-campaigns'
  | 'ambassador-hub'
  | 'drafts'
  | 'casting'
  | 'content-hub'
  | 'ad-studios'
  | 'audio-dubbing'
  | 'video-ad-studio'
  | 'strategies'
  | 'campaigns'
  | 'campaign-plan'
  | 'campaign-calendar'
  | 'messages'
  | 'brand-lift'
  | 'settings'
  | 'team'
  | 'ad-accounts'
  | 'trophies'
  | 'dev-mode'
  /* Agent pages — the Omni Ad cowork module rendered inside the platform shell. */
  | 'agent-desk'
  | 'agent-kickoff'
  | 'agent-chats'
  | 'agent-tasks'
  | 'agent-approvals'
  | 'agent-team'
  | 'agent-integrations'
  | 'agent-brand'
  | 'agent-brand-pulse'
  | 'agent-brand-id'
  | 'agent-brand-promotables'
  | 'agent-brand-audiences'
  | 'agent-brand-competitors'
  | 'agent-brand-library'
  | 'agent-wiki'
  | 'agent-deliverables'
  | 'agent-dashboards'
  | 'agent-reports'
  | 'agent-media-plans'
  | 'agent-competitor-ads'
  | 'agent-ads-library'
  | 'agent-radar-library'
  | 'agent-canvas'
  | 'agent-tiktok-trends';

/** Agent pages, in sidebar order. Each renders `CoworkPage` embedded at the mapped view. */
export const AGENT_PAGES = [
  'agent-chats',
  'agent-tasks',
  'agent-approvals',
  'agent-team',
  'agent-integrations',
  'agent-brand',
  'agent-brand-pulse',
  'agent-brand-id',
  'agent-brand-promotables',
  'agent-brand-audiences',
  'agent-brand-competitors',
  'agent-brand-library',
  'agent-wiki',
  'agent-deliverables',
  'agent-dashboards',
  'agent-reports',
] as const satisfies readonly CurrentPage[];

export function isAgentPage(page: CurrentPage): boolean {
  return (AGENT_PAGES as readonly string[]).includes(page);
}

/** URL segment under `/agents/…` ⇄ page, and the cowork view each page renders. */
const AGENT_PAGE_BY_SEGMENT: Record<string, CurrentPage> = {
  desk: 'agent-desk',
  kickoff: 'agent-kickoff',
  chats: 'agent-chats',
  tasks: 'agent-tasks',
  approvals: 'agent-approvals',
  team: 'agent-team',
  integrations: 'agent-integrations',
  brand: 'agent-brand',
  'pulse': 'agent-brand-pulse',
  'brand-id': 'agent-brand-id',
  'promotables': 'agent-brand-promotables',
  'audiences': 'agent-brand-audiences',
  'competitors': 'agent-brand-competitors',
  'library': 'agent-brand-library',
  wiki: 'agent-wiki',
  deliverables: 'agent-deliverables',
  dashboards: 'agent-dashboards',
  reports: 'agent-reports',
  'media-plans': 'agent-media-plans',
  'competitor-ads': 'agent-competitor-ads',
  'ads-library': 'agent-ads-library',
  'radar': 'agent-radar-library',
  'canvas': 'agent-canvas',
  'tiktok-trends': 'agent-tiktok-trends',
};

export const AGENT_PAGE_VIEW: Record<string, string> = {
  'agent-chats': 'chats',
  'agent-tasks': 'tasks',
  'agent-approvals': 'approvals',
  'agent-team': 'agents',
  'agent-integrations': 'integrations',
  'agent-brand': 'brand-pulse',
  'agent-brand-pulse': 'brand-pulse',
  'agent-brand-id': 'brand-id',
  'agent-brand-promotables': 'brand-promotables',
  'agent-brand-audiences': 'brand-audiences',
  'agent-brand-competitors': 'brand-competitors',
  'agent-brand-library': 'brand-library',
  'agent-wiki': 'brand-wiki',
  'agent-deliverables': 'deliverables',
  'agent-dashboards': 'dashboards',
};

/** Platform pages hidden unless `user.show_cowork` is true (api-v2 staff flag). */
const COWORK_GATED_PLATFORM_PAGES: readonly CurrentPage[] = [
  'campaign-plan',
  'messages',
  'brand-pulse',
  'audience-insights',
  'news-monitor',
  'ad-studios',
  'audio-dubbing',
  'video-ad-studio',
  'agent-desk',
  'agent-kickoff',
  'agent-chats',
  'agent-tasks',
  'agent-approvals',
  'agent-team',
  'agent-integrations',
  'agent-brand',
  'agent-brand-pulse',
  'agent-brand-id',
  'agent-brand-promotables',
  'agent-brand-audiences',
  'agent-brand-competitors',
  'agent-brand-library',
  'agent-wiki',
  'agent-deliverables',
  'agent-dashboards',
  'agent-reports',
  'agent-media-plans',
  'agent-competitor-ads',
  'agent-ads-library',
  'agent-radar-library',
  'agent-canvas',
  'agent-tiktok-trends',
];

export function isCoworkGatedPlatformPage(page: CurrentPage): boolean {
  return (COWORK_GATED_PLATFORM_PAGES as readonly string[]).includes(page);
}

/** Trends + Evolve top-level pages — staff only (`user.show_evolve`). */
const PREVIEW_GATED_PLATFORM_PAGES: readonly CurrentPage[] = [
  'trends',
  'performance',
  'brand-lift',
  'marketing-mix-model-mmm',
  'trophies',
];

/** Whether the page is Trends or Evolve — staff only (`user.show_evolve`). */
export function isPreviewGatedPlatformPage(page: CurrentPage): boolean {
  return (PREVIEW_GATED_PLATFORM_PAGES as readonly string[]).includes(page);
}

export interface AppRouteParams {
  activeMode: AppMode;
  currentPage: CurrentPage;
  creatorId?: string;
  listId?: string;
  campaignId?: string;
  subTab?: CampaignSubTab;
  beatId?: string;
  trophyId?: string;
  contentHubMediaId?: string;
}

/** Maps a `CampaignSubTab` to the URL path segment (kebab-case where needed). */
export function campaignSubTabToUrlSegment(tab: CampaignSubTab): string {
  if (tab === 'contentApproval') return 'content-approval';
  if (tab === 'briefsBeats') return 'briefs-beats';
  return tab;
}

/** Maps URL segment to canonical sub-tab; legacy `/.../suggestions` → `profiles`; `/.../drafts` → `contentApproval`. */
export function normalizeCampaignSubTabSegment(segment: string | undefined): CampaignSubTab {
  if (!segment) return 'overview';
  if (segment === 'suggestions') return 'profiles';
  if (segment === 'drafts' || segment === 'content-approval') return 'contentApproval';
  if (segment === 'briefs-beats') return 'briefsBeats';
  if (segment === 'summary') return 'results';
  if (
    segment === 'overview' ||
    segment === 'profiles' ||
    segment === 'collaborations' ||
    segment === 'publications' ||
    segment === 'results'
  ) {
    return segment;
  }
  return 'overview';
}

/**
 * Parses the app pathname (no leading slash) and returns activeMode, currentPage, and any object IDs.
 * Used by App to derive state from URL. Unknown paths resolve to dashboard.
 */
export function parseAppPath(pathname: string): AppRouteParams {
  const path = pathname.startsWith('/') ? pathname.slice(1) : pathname;
  const segments = path ? path.split('/').filter(Boolean) : [];

  // Legacy beatly-web-app paths: /connect/... → same semantics as /... in omni
  if (segments[0] === 'connect') {
    const rest = segments.slice(1);
    const innerPath = rest.length ? `/${rest.join('/')}` : '/';
    return parseAppPath(innerPath);
  }

  // Mode-level
  if (segments[0] === 'chat') {
    return { activeMode: 'chat', currentPage: 'dashboard' };
  }
  if (segments[0] === 'cowork') {
    // Legacy: /cowork was the standalone prototype. The real agent surface
    // lives at /agents/*; old bookmarks land on chats.
    return { activeMode: 'platform', currentPage: 'agent-chats' };
  }

  // Platform routes
  if (segments.length === 0 || segments[0] === 'dashboard') {
    return { activeMode: 'platform', currentPage: 'dashboard' };
  }

  switch (segments[0]) {
    case 'agents': {
      const page = AGENT_PAGE_BY_SEGMENT[segments[1] ?? ''];
      return { activeMode: 'platform', currentPage: page ?? 'agent-chats' };
    }
    case 'messages':
      return { activeMode: 'platform', currentPage: 'messages' };
    case 'brand-pulse':
      return { activeMode: 'platform', currentPage: 'brand-pulse' };
    case 'content-hub': {
      const raw = segments[1]?.trim();
      if (raw) {
        try {
          return {
            activeMode: 'platform',
            currentPage: 'content-hub',
            contentHubMediaId: decodeURIComponent(raw),
          };
        } catch {
          return {
            activeMode: 'platform',
            currentPage: 'content-hub',
            contentHubMediaId: raw,
          };
        }
      }
      return { activeMode: 'platform', currentPage: 'content-hub' };
    }
    case 'creators':
      if (segments.length >= 3 && segments[1] === 'lists' && segments[2] === 'ambassadors') {
        return { activeMode: 'platform', currentPage: 'creators' };
      }
      if (segments.length >= 2 && segments[1] === 'search') {
        return { activeMode: 'platform', currentPage: 'search-results' };
      }
      if (segments.length >= 3 && segments[1] === 'creatorlist' && segments[2]) {
        return {
          activeMode: 'platform',
          currentPage: 'discovery-creatorlist',
          listId: segments[2],
        };
      }
      if (segments.length >= 2) {
        return {
          activeMode: 'platform',
          currentPage: 'creator-profile',
          creatorId: segments[1],
        };
      }
      return { activeMode: 'platform', currentPage: 'creators' };
    case 'creator-lists':
      if (segments.length >= 2) {
        return {
          activeMode: 'platform',
          currentPage: 'creatorlist-detail',
          listId: segments[1],
        };
      }
      return { activeMode: 'platform', currentPage: 'creatorslist' };
    case 'audience-insights':
      return { activeMode: 'platform', currentPage: 'audience-insights' };
    case 'trends':
      return { activeMode: 'platform', currentPage: 'trends' };
    case 'news-monitor':
      return { activeMode: 'platform', currentPage: 'news-monitor' };
    case 'ad-studio':
      if (segments[1] === 'video') {
        return { activeMode: 'platform', currentPage: 'video-ad-studio' };
      }
      if (segments[1] === 'dubbing') {
        return { activeMode: 'platform', currentPage: 'audio-dubbing' };
      }
      return { activeMode: 'platform', currentPage: 'ad-studios' };
    case 'campaigns':
      if (segments[1] === 'new') {
        return { activeMode: 'platform', currentPage: 'campaigns' };
      }
      if (segments[1] === 'calendar') {
        return { activeMode: 'platform', currentPage: 'campaign-calendar' };
      }
      if (segments[1] === 'plan') {
        return { activeMode: 'platform', currentPage: 'campaign-plan' };
      }
      if (segments[1] && segments[1] !== 'new' && segments[1] !== 'plan') {
        const campaignId = segments[1];
        if (
          (segments[2] === 'results' || segments[2] === 'summary') &&
          segments[3] === 'report'
        ) {
          return {
            activeMode: 'platform',
            currentPage: 'see-campaigns',
            campaignId,
            subTab: 'results',
          };
        }
        /** Omitted segment → `undefined` so clients can apply local defaults (e.g. last-opened tab). */
        const subTab =
          segments[2] !== undefined ? normalizeCampaignSubTabSegment(segments[2]) : undefined;
        const beatId =
          segments.length >= 5 && segments[3] === 'beat' && segments[4]
            ? segments[4]
            : undefined;
        return {
          activeMode: 'platform',
          currentPage: 'see-campaigns',
          campaignId,
          subTab,
          beatId,
        };
      }
      return { activeMode: 'platform', currentPage: 'see-campaigns' };
    case 'ambassador-hub':
      return { activeMode: 'platform', currentPage: 'ambassador-hub' };
    case 'drafts':
      return { activeMode: 'platform', currentPage: 'drafts' };
    case 'casting':
      return { activeMode: 'platform', currentPage: 'casting' };
    case 'strategies':
      return { activeMode: 'platform', currentPage: 'strategies' };
    case 'performance':
      return { activeMode: 'platform', currentPage: 'performance' };
    case 'brand-lift':
      return { activeMode: 'platform', currentPage: 'brand-lift' };
    case 'marketing-mix-model':
      return { activeMode: 'platform', currentPage: 'marketing-mix-model-mmm' };
    case 'awards':
      return {
        activeMode: 'platform',
        currentPage: 'trophies',
        trophyId: segments[1] && segments[1].length > 0 ? segments[1] : undefined,
      };
    case 'settings':
      return { activeMode: 'platform', currentPage: 'settings' };
    case 'team':
      return { activeMode: 'platform', currentPage: 'team' };
    // `/ad-accounts` and `/ad-accounts/connect` (OAuth return with ?session=)
    case 'ad-accounts':
      return { activeMode: 'platform', currentPage: 'ad-accounts' };
    case 'dev-mode':
      return { activeMode: 'platform', currentPage: 'dev-mode' };
    default:
      return { activeMode: 'platform', currentPage: 'dashboard' };
  }
}

/**
 * Returns the path string for a given page and optional params (for object-specific pages).
 * Used for programmatic navigation (navigate(getPathForPage(...))).
 */
export function getPathForPage(
  page: CurrentPage,
  params?: {
    creatorId?: string;
    listId?: string;
    campaignId?: string;
    subTab?: CampaignSubTab;
    beatId?: string;
    trophyId?: string;
    contentHubMediaId?: string;
  }
): string {
  if (page === 'discovery-creatorlist' && params?.listId) {
    return `/creators/creatorlist/${encodeURIComponent(params.listId)}`;
  }
  if (page === 'creator-profile' && params?.creatorId) {
    return `/creators/${params.creatorId}`;
  }
  if (page === 'creatorlist-detail' && params?.listId) {
    return `/creator-lists/${params.listId}`;
  }
  if (page === 'see-campaigns' && params?.campaignId) {
    // When beatId is present we need /campaigns/:id/:subTab/beat/:beatId, so subTab must be in the path
    const subTab = params.subTab ?? 'overview';
    const subSeg = campaignSubTabToUrlSegment(subTab);
    const sub = params?.beatId ? `/${subSeg}` : subTab !== 'overview' ? `/${subSeg}` : '';
    const beat = params?.beatId ? `/beat/${params.beatId}` : '';
    return `/campaigns/${params.campaignId}${sub}${beat}`;
  }
  if (page === 'trophies' && params?.trophyId) {
    return `/awards/${params.trophyId}`;
  }
  if (page === 'content-hub' && params?.contentHubMediaId) {
    return `/content-hub/${encodeURIComponent(params.contentHubMediaId)}`;
  }

  const map: Record<CurrentPage, string> = {
    dashboard: '/',
    'news-monitor': '/news-monitor',
    performance: '/performance',
    'brand-pulse': '/brand-pulse',
    'marketing-mix-model-mmm': '/marketing-mix-model',
    creators: '/creators',
    creatorslist: '/creator-lists',
    'creatorlist-detail': '/creator-lists',
    'creator-profile': '/creators',
    'discovery-creatorlist': '/creators',
    'search-results': '/creators/search',
    'audience-insights': '/audience-insights',
    trends: '/trends',
    'see-campaigns': '/campaigns',
    'ambassador-hub': '/ambassador-hub',
    drafts: '/drafts',
    casting: '/casting',
    'content-hub': '/content-hub',
    'ad-studios': '/ad-studio',
    'audio-dubbing': '/ad-studio/dubbing',
    'video-ad-studio': '/ad-studio/video',
    strategies: '/strategies',
    campaigns: '/campaigns/new',
    'campaign-plan': '/campaigns/plan',
    'campaign-calendar': '/campaigns/calendar',
    messages: '/messages',
    'brand-lift': '/brand-lift',
    settings: '/settings',
    team: '/team',
    'ad-accounts': '/ad-accounts',
    trophies: '/awards',
    'dev-mode': '/dev-mode',
    'agent-desk': '/agents/desk',
    'agent-kickoff': '/agents/kickoff',
    'agent-chats': '/agents/chats',
    'agent-tasks': '/agents/tasks',
    'agent-approvals': '/agents/approvals',
    'agent-team': '/agents/team',
    'agent-integrations': '/agents/integrations',
    'agent-brand': '/agents/pulse',
    'agent-brand-pulse': '/agents/pulse',
    'agent-brand-id': '/agents/brand-id',
    'agent-brand-promotables': '/agents/promotables',
    'agent-brand-audiences': '/agents/audiences',
    'agent-brand-competitors': '/agents/competitors',
    'agent-brand-library': '/agents/library',
    'agent-wiki': '/agents/wiki',
    'agent-deliverables': '/agents/deliverables',
    'agent-dashboards': '/agents/dashboards',
    'agent-reports': '/agents/reports',
    'agent-media-plans': '/agents/media-plans',
    'agent-competitor-ads': '/agents/competitor-ads',
    'agent-ads-library': '/agents/ads-library',
    'agent-radar-library': '/agents/radar',
    'agent-canvas': '/agents/canvas',
    'agent-tiktok-trends': '/agents/tiktok-trends',
  };
  return map[page] ?? '/';
}

/** Path for mode-only (chat/cowork). */
export function getPathForMode(mode: AppMode): string {
  if (mode === 'chat') return '/chat';
  if (mode === 'cowork') return '/cowork';
  return '/';
}
